import { useState, useEffect } from 'react';
import { SUPPORT_TICKET_URL, TERMS_URL, PRIVACY_URL } from '@/config/urls';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { dlog } from '@/utils/devlog';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<{ message: string; type?: string }>({
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { signIn, user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  // Redirect to dashboard if already logged in
  useEffect(() => {
    if (user && !loading) {
      const redirectTo = searchParams.get('redirectTo') || '/dashboard';
      // console.log('[Login] User authenticated, redirecting to:', redirectTo);
      // dlog('[Login] Redirect', {
      //   user: user.id,
      //   metadata: user.user_metadata,
      //   redirectTo,
      // });
      navigate(decodeURIComponent(redirectTo), { replace: true });
    }
  }, [user, loading, navigate, searchParams]);

  // Handle login form submission
  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError({ message: '' });
    setIsSubmitting(true);

    try {
      // console.log('[Login] Attempting sign-in with email:', email);
      // dlog('[Login] SignIn', { email });
      const { error: signInError } = await signIn(email, password);
      if (signInError) {
        // console.error('[Login] Sign-in error:', signInError);
        // dlog('[Login] SignIn error', { error: signInError.message });
        let displayMessage = signInError.message;
        let errorType = signInError.code; // Use .code (e.g., 'user_banned', 'invalid_credentials')
        if (
          errorType === 'invalid_credentials' ||
          displayMessage === 'Invalid login credentials'
        ) {
          displayMessage =
            'Invalid email or password. If you believe your credentials are correct, your account may be disabled—please contact support.';
          errorType = 'invalid_credentials';
        } else if (
          errorType === 'user_banned' ||
          displayMessage === 'User is banned'
        ) {
          displayMessage =
            'Your account has been disabled. Please contact support for assistance.';
          errorType = 'banned';
        }
        setError({ message: displayMessage, type: errorType });
        setIsSubmitting(false);
        return;
      }
      // console.log('[Login] Sign-in successful, redirect handled by useEffect');
      // dlog('[Login] SignIn success', { email });

      // Redirect handled by useEffect
    } catch (err) {
      console.error('[Login] Unexpected sign-in error:', err);
      // dlog('[Login] Unexpected error', { error: err });
      setError({
        message: 'An unexpected error occurred during login',
        type: 'unexpected',
      });
      setIsSubmitting(false);
    }
  };

  // Handle support link click
  const handleSupportLinkClick = () => {
    window.open(SUPPORT_TICKET_URL, '_blank');
    setError({ message: '' }); // Clear error
    window.location.reload(); // Refresh page
  };

  return (
    <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/30 to-background p-4'>
      <div className='w-full max-w-md space-y-8'>
        {/* Logo and Header */}
        <div className='text-center space-y-4'>
          <div className='flex items-center justify-center'>
            <img
              src='/images/8e8b8d0c-2b0f-47d3-aed3-98e19c548c8d.png'
              alt='CallSparker'
              className='h-16'
            />
          </div>
          <p className='text-muted-foreground'>
            Power your B2B voice prospecting with AI
          </p>
        </div>

        {/* Auth Form */}
        <Card className='border-2'>
          <CardHeader className='space-y-1'>
            <CardTitle className='text-2xl font-semibold'>Sign In</CardTitle>
            <CardDescription>
              Enter your credentials to access your CallSparker dashboard
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error.message && (
              <Alert variant='destructive' className='mb-4'>
                <AlertDescription>
                  {error.type === 'deactivated' ? (
                    <p>
                      Your account is deactivated due to subscription
                      cancellation. Please{' '}
                      <button
                        type='button'
                        onClick={handleSupportLinkClick}
                        className='text-blue-500 underline hover:text-blue-700'
                      >
                        contact support
                      </button>{' '}
                      to reactivate.
                    </p>
                  ) : error.type === 'invalid_credentials' ? (
                    <p>
                      Invalid email or password. If you believe your credentials
                      are correct, your account may be disabled—please{' '}
                      <button
                        type='button'
                        onClick={handleSupportLinkClick}
                        className='text-blue-500 underline hover:text-blue-700'
                      >
                        contact support
                      </button>
                      .
                    </p>
                  ) : error.type === 'banned' ? (
                    <p>
                      Your account has been disabled. Please{' '}
                      <button
                        type='button'
                        onClick={handleSupportLinkClick}
                        className='text-blue-500 underline hover:text-blue-700'
                      >
                        contact support
                      </button>{' '}
                      for assistance.
                    </p>
                  ) : (
                    error.message
                  )}
                </AlertDescription>
              </Alert>
            )}
            {loading && !isSubmitting && (
              <div className='mb-4 text-sm text-muted-foreground'>
                Loading authentication...
              </div>
            )}
            <form onSubmit={handleAuth} className='space-y-4'>
              <div className='space-y-2'>
                <Label htmlFor='email'>Email</Label>
                <Input
                  id='email'
                  type='email'
                  placeholder='your@email.com'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className='h-11'
                  disabled={isSubmitting}
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='password'>Password</Label>
                <div className='relative'>
                  <Input
                    id='password'
                    type={showPassword ? 'text' : 'password'}
                    placeholder='Enter your password'
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className='h-11 pr-10'
                    disabled={isSubmitting}
                  />
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    className='absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent'
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className='h-4 w-4 text-muted-foreground' />
                    ) : (
                      <Eye className='h-4 w-4 text-muted-foreground' />
                    )}
                  </Button>
                </div>
              </div>

              <Button
                type='submit'
                className='w-full h-11'
                disabled={isSubmitting || loading}
              >
                {isSubmitting ? 'Processing...' : 'Sign In'}
              </Button>
            </form>
            <p className='text-center text-sm text-muted-foreground mt-4'>
              <Link
                to='/forgot-password'
                className='text-blue-500 hover:underline'
              >
                Forgot your password?
              </Link>
            </p>
          </CardContent>
        </Card>

        {/* Private Beta Notice */}
        <Card className='bg-muted/50 border-dashed'>
          <CardContent className='pt-6'>
            <div className='text-center space-y-3'>
              <p className='text-sm font-medium text-muted-foreground'>
                Private Beta
              </p>
              <p className='text-xs text-muted-foreground'>
                CallSparker is currently in private beta. Contact us for access
                information.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Legal Links */}
        <p className='text-center text-xs text-muted-foreground'>
          By signing in, you agree to our{' '}
          <a
            href={TERMS_URL}
            target='_blank'
            rel='noopener noreferrer'
            className='underline hover:text-foreground'
          >
            Terms of Service
          </a>{' '}
          and{' '}
          <a
            href={PRIVACY_URL}
            target='_blank'
            rel='noopener noreferrer'
            className='underline hover:text-foreground'
          >
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export default Login;
